<script setup>
import { computed, ref } from 'vue';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  invoices: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  isSubmitting: { type: Boolean, default: false },
  errorCode: { type: String, default: '' },
  minimumDate: { type: String, required: true },
});

const emit = defineEmits(['retry', 'submit', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const selectedInvoiceId = ref('');
const limitOn = ref('');
const comment = ref('');

const invoiceOptions = computed(() =>
  props.invoices.map(invoice => ({
    value: invoice.external_id,
    label: t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.INVOICE_OPTION', {
      id: invoice.external_id,
      dueOn: format(parseISO(invoice.due_on), 'dd/MM/yyyy'),
      total: Number(invoice.total).toFixed(2),
    }),
  }))
);

const errorMessage = computed(() => {
  const messages = {
    payment_promise_already_exists: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.PAYMENT_PROMISE_ALREADY_EXISTS'
    ),
    invoice_not_found: t('CONVERSATION_SIDEBAR.ERP.ERRORS.INVOICE_NOT_FOUND'),
    invalid_request: t('CONVERSATION_SIDEBAR.ERP.ERRORS.INVALID_REQUEST'),
    authentication_error: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.AUTHENTICATION_ERROR'
    ),
    authorization_error: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.AUTHORIZATION_ERROR'
    ),
    configuration_error: t(
      'CONVERSATION_SIDEBAR.ERP.ERRORS.CONFIGURATION_ERROR'
    ),
    timeout: t('CONVERSATION_SIDEBAR.ERP.ERRORS.TIMEOUT'),
  };

  return props.errorCode
    ? messages[props.errorCode] ||
        t('CONVERSATION_SIDEBAR.ERP.ERRORS.PROVIDER_ERROR')
    : '';
});

const hasSelectedInvoice = computed(() =>
  props.invoices.some(
    invoice => invoice.external_id === selectedInvoiceId.value
  )
);
const canSubmit = computed(
  () =>
    !props.isLoading &&
    !props.isSubmitting &&
    hasSelectedInvoice.value &&
    limitOn.value &&
    limitOn.value >= props.minimumDate
);

const reset = () => {
  selectedInvoiceId.value = '';
  limitOn.value = '';
  comment.value = '';
};

const open = () => {
  reset();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();
const closeAfterSuccess = () => {
  reset();
  close();
};
const submit = () => {
  if (!canSubmit.value) return;

  emit('submit', {
    invoiceId: selectedInvoiceId.value,
    limitOn: limitOn.value,
    comment: comment.value,
  });
};

defineExpose({ open, close, closeAfterSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.TITLE')"
    :description="t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="emit('close')"
  >
    <div
      v-if="isLoading"
      class="flex min-h-32 flex-col items-center justify-center gap-3 text-sm text-n-slate-11"
    >
      <Spinner :size="20" />
      <span>{{ t('CONVERSATION_SIDEBAR.ERP.INVOICES.LOADING') }}</span>
    </div>
    <div
      v-else-if="errorCode && !invoices.length && !selectedInvoiceId"
      class="flex min-h-32 flex-col items-center justify-center gap-3 text-center text-sm text-n-slate-11"
    >
      <p class="mb-0">{{ errorMessage }}</p>
      <Button
        data-testid="erp-payment-promise-retry"
        sm
        faded
        slate
        icon="i-lucide-refresh-ccw"
        :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.RETRY')"
        @click="emit('retry')"
      />
    </div>
    <p
      v-else-if="!invoices.length && !errorCode"
      class="min-h-32 mb-0 flex items-center justify-center text-center text-sm text-n-slate-11"
    >
      {{ t('CONVERSATION_SIDEBAR.ERP.INVOICES.EMPTY') }}
    </p>
    <div v-else class="flex flex-col gap-4">
      <div v-if="errorCode" class="flex items-center justify-between gap-3">
        <p class="mb-0 text-sm text-n-ruby-11">{{ errorMessage }}</p>
        <Button
          v-if="!invoices.length"
          data-testid="erp-payment-promise-retry"
          sm
          faded
          slate
          icon="i-lucide-refresh-ccw"
          :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.RETRY')"
          @click="emit('retry')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.INVOICE') }}
        </span>
        <ComboBox
          v-model="selectedInvoiceId"
          data-testid="erp-promise-invoice"
          :options="invoiceOptions"
          :display-label="selectedInvoiceId"
          :disabled="isSubmitting || !invoices.length"
          :placeholder="
            t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.SELECT_INVOICE')
          "
          :search-placeholder="
            t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.SEARCH_INVOICE')
          "
        />
      </div>
      <Input
        v-model="limitOn"
        data-testid="erp-promise-date"
        type="date"
        :min="minimumDate"
        :disabled="isSubmitting"
        :label="t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.LIMIT_ON')"
      />
      <TextArea
        v-model="comment"
        data-testid="erp-promise-comment"
        :disabled="isSubmitting"
        :label="t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.COMMENT')"
        :placeholder="
          t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.COMMENT_PLACEHOLDER')
        "
      />
    </div>
    <template #footer>
      <div class="flex w-full items-center justify-between gap-3">
        <Button
          class="w-full"
          type="button"
          faded
          slate
          :disabled="isSubmitting"
          :label="t('CONVERSATION_SIDEBAR.ERP.ACTIONS.CANCEL')"
          @click="close"
        />
        <Button
          data-testid="erp-promise-confirm"
          class="w-full"
          type="button"
          blue
          :is-loading="isSubmitting"
          :disabled="!canSubmit"
          :label="t('CONVERSATION_SIDEBAR.ERP.PAYMENT_PROMISE.CONFIRM')"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
